import React, { ReactNode, useCallback, useEffect, useState } from 'react';
import GfModal from '../gfcomponents/GfModal/GfModal';
import { useBlocker, useNavigate } from 'react-router-dom';

export type RouterPromptProps = {
  /**
   * The message to show when navigating to a new route in the app.
   *
   * Default: "There are unsaved changes. Are you sure you want to leave?" */
  children?: ReactNode;
  /**
   * The message to show when a tab or window is being closed.
   *
   * Default: "Leaving this page will cause you to lose your unsaved changes."
   */
  browserMessage?: string | (() => string);
  /** Default: "You have unsaved changes" */
  title?: string;
  /** Default: "Yes" */
  primaryText?: string;
  /** Default: undefined */
  secondaryText?: string;
  /** Default: "No" */
  tertiaryText?: string;
  /** True, to show the prompt, false otherwise. */
  when?: boolean;
  /** Function to call when the user confirms navigation and wants to save changes. */
  onPrimaryClick?: () => Promise<void>;
  /** Function to call when the user confirms navigation and does not want to save changes. */
  onSecondaryClick?: () => void;
};

/**
 * Prompts the user to confirm their navigation or closure when there are changes in the
 * current route.
 *
 * Will also prompt the use
 */
export default function RouterPrompt(props: RouterPromptProps) {
  const { browserMessage, primaryText, secondaryText, tertiaryText, title, when, children, onPrimaryClick, onSecondaryClick } = props;
  const blocker = useBlocker(when);
  const navigate = useNavigate();
  const content = children ?? 'There are unsaved changes. Are you sure you want to leave?';
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const beforeUnload = (ev: BeforeUnloadEvent) => {
      if (!when) {
        return;
      }

      ev.preventDefault();
      // legacy support
      ev.returnValue =
        typeof browserMessage === 'function' ? browserMessage() : browserMessage ?? 'Leaving this page will cause you to lose your unsaved changes.';
    };

    window.addEventListener('beforeunload', beforeUnload);

    return () => window.removeEventListener('beforeunload', beforeUnload);
  }, [when, browserMessage]);

  const handlePrimaryClick = useCallback(async () => {
    if (onPrimaryClick) {
      setLoading(true);
      await onPrimaryClick();
      setLoading(false);
      // there is a bug in react-router that causes blocker.proceed() to not work after calling another async function
      // this is the recommended workaround to prevent issues with the blocker proceeding too fast
      const url = blocker.location.pathname + blocker.location.search;
      blocker.reset();
      navigate(url);
    } else {
      blocker.proceed();
    }
  }, [blocker, navigate, onPrimaryClick]);

  const handleSecondaryClick = useCallback(() => {
    onSecondaryClick?.();
    blocker.proceed();
  }, [blocker, onSecondaryClick]);

  const handleTertiaryClick = useCallback(async () => {
    blocker.reset();
  }, [blocker]);

  if (blocker.state === 'unblocked') {
    return null;
  }

  return (
    <GfModal
      open={when}
      title={title ?? 'Unsaved Changes'}
      primaryText={primaryText ?? 'Yes'}
      secondaryText={secondaryText}
      tertiaryText={tertiaryText ?? 'No'}
      onPrimaryClick={handlePrimaryClick}
      onSecondaryClick={handleSecondaryClick}
      onTertiaryClick={handleTertiaryClick}
      onClose={handleTertiaryClick}
      loading={loading}
    >
      {content}
    </GfModal>
  );
}
